import { isAfter } from "date-fns";
import * as yup from "yup";
import {
	CommodityFixings,
	PPAShapeTypes,
	PricingAddOn,
} from "./PricingRequestStepper.slice";
import {
	MAXIMUM_TYPES_IN_ONE_GO,
	PayoutMethod,
} from "./PricingRequestStepper.constant";

const validateChapter51Options = (
	chapter51Switch: boolean[],
	schema: yup.NumberSchema | yup.StringSchema
) => {
	if (!chapter51Switch[0]) return schema;
	return yup
		.mixed()
		.required("Chapter51 type & number of hours are required.");
};

const validateClickCount = (params: any, schema: yup.NumberSchema) => {
	const [clickGranularity, clickOptionSwitch] = params;
	if (!!clickOptionSwitch && clickGranularity.length > 0)
		return yup
			.number()
			.required("Number of clicks is a required field")
			.min(1, "Please enter a value between 1 and 10")
			.max(10, "Please enter a value between 1 and 10");
	return schema;
};
const validateClickGranularity = (params: any, schema: yup.Schema) => {
	const [clickOptionSwitch] = params;

	if (!clickOptionSwitch) return schema;
	return yup.array().min(1, "Choosing click granularity is required");
};

function validateEndDateAfterStartDate(values: any) {
	const isValid = isAfter(values.endDate, values.startDate);
	if (!isValid) {
		// @ts-ignore
		return this.createError({
			path: "startDate",
			message: "There should be at least one day between start and end",
		});
	}
	return true;
}

const validateCapFloorPayoutMethod = (params: any, ctx: any) => {
	if (!params.hasPayoutMethod) return true;
	if (!params.payoutMethod) return true;
	if (
		params.payoutMethod === PayoutMethod.COLLAR &&
		params.floor >= params.cap
	) {
		return ctx.createError({
			path: "cap",
			message:
				"Cap should be greater than floor if payout method is collar",
		});
	}
	return true;
};

const validateCapFloorPayoutMethodIntraDay = (params: any, ctx: any) => {
	if (!params.hasPayoutMethodIntraDay) return true;
	if (!params.payoutMethodIntraDay) return true;
	if (
		params.payoutMethodIntraDay === PayoutMethod.COLLAR &&
		params.floorIntraDay >= params.capIntraDay
	) {
		return ctx.createError({
			path: "capIntraDay",
			message:
				"Cap should be greater than floor if payout method is collar",
		});
	}
	return true;
};

export function alphaOrBetaSelectedIfIndexesSelected(values: any) {
	if (values.pricingType?.length) {
		const hasIndexPrice =
			values.pricingType.findIndex(
				(index: any) => index.has_result_options
			) >= 0;
		if (hasIndexPrice) {
			if (!values.alpha && !values.beta && !values.alphaAndBeta) {
				// @ts-ignore
				return this.createError({
					path: "alpha",
					message: "At least one option must be selected ",
				});
			}
		}
	}
	return true;
}

export const firstOfNextYear = new Date(new Date().getFullYear() + 1, 0, 1);

const capFloorSchema = {
	hasFixPrice: yup.boolean(),
	//Cap/Floor intraday
	hasPayoutMethodIntraDay: yup.boolean(),
	payoutMethodIntraDay: yup.string().when("hasPayoutMethodIntraDay", {
		is: (hasPayoutMethodIntraDay: boolean) => hasPayoutMethodIntraDay,
		then: (schema) => schema.required("Payout method is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	capIntraDay: yup.number().when("payoutMethodIntraDay", {
		is: (payoutMethodIntraDay: string | null) =>
			payoutMethodIntraDay === PayoutMethod.CAP ||
			payoutMethodIntraDay === PayoutMethod.COLLAR,
		then: (schema) => schema.required("Cap is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	floorIntraDay: yup.number().when("payoutMethodIntraDay", {
		is: (payoutMethodIntraDay: string | null) =>
			payoutMethodIntraDay === PayoutMethod.FLOOR ||
			payoutMethodIntraDay === PayoutMethod.COLLAR,
		then: (schema) => schema.required("Floor is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	//Cap/Floor
	hasPayoutMethod: yup.boolean(),
	payoutMethod: yup.string().when("hasPayoutMethod", {
		is: (hasPayoutMethod: boolean) => hasPayoutMethod,
		then: (schema) => schema.required("Payout method is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	cap: yup.number().when("payoutMethod", {
		is: (payoutMethod: string | null) =>
			payoutMethod === PayoutMethod.CAP ||
			payoutMethod === PayoutMethod.COLLAR,
		then: (schema) => schema.required("Cap is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
	floor: yup.number().when("payoutMethod", {
		is: (payoutMethod: string | null) =>
			payoutMethod === PayoutMethod.FLOOR ||
			payoutMethod === PayoutMethod.COLLAR,
		then: (schema) => schema.required("Floor is required"),
		otherwise: (schema) => schema.notRequired(),
	}),
};

export const pricingRequestStepSchema = yup
	.object()
	.shape({
		pricingType: yup
			.array()
			.of(
				yup.object().shape({
					name: yup
						.string()
						.required("Pricing type is a required field"),
				})
			)
			.min(1, "Pricing type must have at least 1 items")
			.max(
				MAXIMUM_TYPES_IN_ONE_GO,
				`Maximum ${MAXIMUM_TYPES_IN_ONE_GO} types in one request, if you need more, do multiple requests`
			),
		hasFixPrice: yup.boolean(),
		gooPricingType: yup
			.array()
			.of(
				yup.object().shape({
					name: yup.string(),
				})
			)
			.max(
				MAXIMUM_TYPES_IN_ONE_GO,
				`Maximum ${MAXIMUM_TYPES_IN_ONE_GO} types in one request, if you need more, do multiple requests`
			),
		startDate: yup
			.date()
			.typeError("Start date is a required field")
			.required("Start date is a required field"),
		endDate: yup
			.date()
			.typeError("End date is a required field")
			.required("End date is a required field"),
		gooStartDate: yup
			.date()
			.typeError("Start date is a required field")
			.required("Start date is a required field"),
		gooEndDate: yup
			.date()
			.typeError("End date is a required field")
			.required("End date is a required field"),
		baseCapacity: yup
			.number()
			.when("pricingType", (pricingType, schema) => {
				if (pricingType.length === 0) {
					return schema;
				}
				const hasBaseProd = pricingType[0].some((p: { name: string }) =>
					p.name.toLowerCase().includes("baseprod")
				);
				return hasBaseProd ? schema.required() : schema;
			})
			.moreThan(0, "Please enter a minimum value greater than 0")
			.max(100, "Please enter a Maximum value of 100"),
		clickGranularity: yup
			.mixed()
			.when(["clickOptionSwitch"], validateClickGranularity),
		gooPowerAdjustment: yup
			.number()
			.min(0, "Please enter a minimum value of 0")
			.max(100, "Please enter a Maximum value of 100"),
		clickCountYear: yup
			.number()
			.when(
				["clickGranularity", "clickOptionSwitch"],
				validateClickCount
			),
		clickCountMonth: yup
			.number()
			.when(
				["clickGranularity", "clickOptionSwitch"],
				validateClickCount
			),
		clickCountQuarter: yup
			.number()
			.when(
				["clickGranularity", "clickOptionSwitch"],
				validateClickCount
			),
		chapter51Type: yup
			.string()
			.when("chapter51Switch", validateChapter51Options),
		provisionType: yup.string().required(),
		spotMarket: yup.string().required(),
		chapter51NumberOfHours: yup
			.number()
			.when("chapter51Switch", validateChapter51Options),
		alpha: yup.boolean(),
		beta: yup.boolean(),
		alphaAndBeta: yup.boolean(),
		isNodal: yup.boolean(),
		// ✅ New fields for negative price
		hasNegativePrice: yup.boolean(),
		negativePriceValue: yup.number().when("hasNegativePrice", {
			is: true,
			then: (schema) =>
				schema.required(
					"pricing.stepper.errors.negative_price_value_is_required"
				),
			otherwise: (schema) => schema.notRequired(),
		}),
		negativePriceThreshold: yup
			.string()
			.when(["hasNegativePrice", "negativePriceIsActive"], {
				is: true,
				then: (schema) =>
					schema.required(
						"pricing.stepper.errors.negative_price_threshold_is_required"
					),
				otherwise: (schema) => schema.notRequired(),
			}),
		negativePriceOperator: yup
			.string()
			.when(["hasNegativePrice", "negativePriceIsActive"], {
				is: true,
				then: (schema) =>
					schema.required(
						"pricing.stepper.errors.negative_price_operator_is_required"
					),
				otherwise: (schema) => schema.notRequired(),
			}),
		negativePriceIndex: yup
			.object()
			.when(["hasNegativePrice", "negativePriceIsActive"], {
				is: true,
				then: (schema) =>
					schema.required(
						"pricing.stepper.errors.negative_price_index_is_required"
					),
				otherwise: (schema) => schema.notRequired(),
			}),
		negativePriceNBConsecutiveBlocks: yup
			.number()
			.when(["hasNegativePrice", "negativePriceIsActive"], {
				is: true,
				then: (schema) =>
					schema
						.typeError(
							"pricing.stepper.errors.negative_price_nb_consecutive_blocks_must_be_a_number"
						)
						.required(
							"pricing.stepper.errors.negative_price_nb_consecutive_blocks_is_required"
						)
						.moreThan(
							0,
							"pricing.stepper.errors.negative_price_nb_consecutive_blocks_must_be_greater_than_zero"
						),
				otherwise: (schema) => schema.notRequired(),
			}),
		// ✅ New fields for curtailability forward
		hasCurtailabilityForward: yup.boolean(),
		curtailabilityForwardThreshold: yup
			.string()
			.when(
				["hasCurtailabilityForward", "curtailabilityForwardIsActive"],
				{
					is: true,
					then: (schema) =>
						schema.required("Negative Price Threshold is required"),
					otherwise: (schema) => schema.notRequired(),
				}
			),
		curtailabilityForwardOperator: yup
			.string()
			.when(
				["hasCurtailabilityForward", "curtailabilityForwardIsActive"],
				{
					is: true,
					then: (schema) =>
						schema.required("Negative Price Operator is required"),
					otherwise: (schema) => schema.notRequired(),
				}
			),
		curtailabilityForwardIndex: yup
			.object()
			.when(
				["hasCurtailabilityForward", "curtailabilityForwardIsActive"],
				{
					is: true,
					then: (schema) =>
						schema.required("Negative Price Index is required"),
					otherwise: (schema) => schema.notRequired(),
				}
			),
		curtailabilityForwardNBConsecutiveBlocks: yup
			.number()
			.when(
				["hasCurtailabilityForward", "curtailabilityForwardIsActive"],
				{
					is: true,
					then: (schema) =>
						schema
							.typeError(
								"Negative Price NBConsecutiveBlocks must be a number"
							)
							.required(
								"Negative Price NBConsecutiveBlocks is required"
							)
							.moreThan(
								0,
								"Negative Price NBConsecutiveBlocks must be greater than 0"
							),
					otherwise: (schema) => schema.notRequired(),
				}
			),
		...capFloorSchema,
		ppaShapeType: yup
			.array()
			.min(1, "PPA shape types must be specified and not empty")
			.required("PPA shape types are required"),
	})
	.test("endDateAfterStartDate", validateEndDateAfterStartDate)
	.test(
		"alphaOrBetaSelectedIfIndexesSelected",
		alphaOrBetaSelectedIfIndexesSelected
	)
	.test("capgreaterthanfloor", validateCapFloorPayoutMethod)
	.test("capgreaterthanfloorintraday", validateCapFloorPayoutMethodIntraDay);

const sharedSchema = {
	startDate: yup
		.date()
		.typeError("Start date is a required field")
		.required("Start date is a required field"),
	endDate: yup
		.date()
		.typeError("End date is a required field")
		.required("End date is a required field"),
	clickGranularity: yup
		.mixed()
		.when(["clickOptionSwitch"], validateClickGranularity),
	clickCountYear: yup
		.number()
		.when(["clickGranularity", "clickOptionSwitch"], validateClickCount),
	clickCountMonth: yup
		.number()
		.when(["clickGranularity", "clickOptionSwitch"], validateClickCount),
	clickCountQuarter: yup
		.number()
		.when(["clickGranularity", "clickOptionSwitch"], validateClickCount),
	provisionType: yup.string().required(),
	spotMarket: yup.string().required(),
};

export const pricingGooOnlyStepSchema = yup
	.object()
	.shape({
		pricingType: yup
			.array()
			.of(
				yup.object().shape({
					name: yup
						.string()
						.required("Pricing type is a required field"),
				})
			)
			.min(1, "Pricing type must have at least 1 items")
			.max(
				MAXIMUM_TYPES_IN_ONE_GO,
				`Maximum ${MAXIMUM_TYPES_IN_ONE_GO} types in one request, if you need more, do multiple requests`
			),
		...sharedSchema,
	})
	.test("endDateAfterStartDate", validateEndDateAfterStartDate);

export const pricingFinancialStepSchema = yup
	.object()
	.shape({
		strikeHasNegativePrice: yup.boolean(),
		commodityHasNegativePrice: yup.boolean(),
		strikePricingType: yup
			.object()
			.shape({
				name: yup.string().required("Pricing type is a required field"),
			})
			.required("Strike Pricing type is a required field"),
		commodityPricingType: yup
			.object()
			.shape({
				name: yup.string().required("Pricing type is a required field"),
			})
			.required("Commodity Pricing type is a required field"),
		negativePriceValue: yup
			.number()
			.when(["strikeHasNegativePrice", "commodityHasNegativePrice"], {
				is: (strike: boolean, commodity: boolean) =>
					strike || commodity,
				then: (schema) =>
					schema.required(
						"pricing.stepper.errors.negative_price_value_is_required"
					),
				otherwise: (schema) => schema.notRequired(),
			}),
		negativePriceThreshold: yup
			.string()
			.when(["strikeHasNegativePrice", "commodityHasNegativePrice"], {
				is: (strike: boolean, commodity: boolean) =>
					strike || commodity,
				then: (schema) =>
					schema.required(
						"pricing.stepper.errors.negative_price_threshold_is_required"
					),
				otherwise: (schema) => schema.notRequired(),
			}),
		negativePriceOperator: yup
			.string()
			.when(["strikeHasNegativePrice", "commodityHasNegativePrice"], {
				is: (strike: boolean, commodity: boolean) =>
					strike || commodity,
				then: (schema) =>
					schema.required(
						"pricing.stepper.errors.negative_price_operator_is_required"
					),
				otherwise: (schema) => schema.notRequired(),
			}),
		negativePriceIndex: yup
			.object()
			.when(["strikeHasNegativePrice", "commodityHasNegativePrice"], {
				is: (strike: boolean, commodity: boolean) =>
					strike || commodity,
				then: (schema) =>
					schema.required(
						"pricing.stepper.errors.negative_price_index_is_required"
					),
				otherwise: (schema) => schema.notRequired(),
			}),
		negativePriceNBConsecutiveBlocks: yup
			.number()
			.when(["strikeHasNegativePrice", "commodityHasNegativePrice"], {
				is: (strike: boolean, commodity: boolean) =>
					strike || commodity,
				then: (schema) =>
					schema
						.typeError(
							"pricing.stepper.errors.negative_price_nb_consecutive_blocks_must_be_a_number"
						)
						.required(
							"pricing.stepper.errors.negative_price_nb_consecutive_blocks_is_required"
						)
						.moreThan(
							0,
							"pricing.stepper.errors.negative_price_nb_consecutive_blocks_must_be_greater_than_zero"
						),
				otherwise: (schema) => schema.notRequired(),
			}),
		...sharedSchema,
		...capFloorSchema,
	})
	.test("endDateAfterStartDate", validateEndDateAfterStartDate)
	.test("capgreaterthanfloor", validateCapFloorPayoutMethod)
	.test("capgreaterthanfloorintraday", validateCapFloorPayoutMethodIntraDay);

export type ClickabilityGranularity = "Year" | "Quarter" | "Month" | "NoSplit";
export interface Clickability {
	granularity: ClickabilityGranularity;
	click_count: number;
}

export type Granularity = "MONTH" | "QUARTER" | "CAL" | "WHOLE_HORIZON";
export interface PricingRequestAPI {
	start_date: string;
	end_date: string;
	pricing_date?: string;
	is_real_time?: boolean;
	granularities: Granularity[];
	clickability?: Clickability[] | undefined;
	alpha: boolean;
	beta: boolean;
	alpha_and_beta: boolean;
	tender_id: number;
	site_ids: number[];
	types: CommodityFixings[] | string[];
	portfolio_ids: number[];
	base_capacity_volume_percentage?: number;
	chapter51_type: string | undefined;
	chapter51_number_of_hours: number | undefined;
	goo_power_adjustment?: number;
	ppa_with_goo: boolean;
	only_goo: boolean;
	goo_granularities?: Granularity[];
	goo_start_date?: string;
	goo_end_date?: string;
	goo_pricing_types?: CommodityFixings[];
	negative_price_value?: number | null;

	is_financial: boolean;
	commodity_pricing_type?: CommodityFixings;
	strike_pricing_type?: CommodityFixings;
	average_type?: string;
	commodity_beta?: number;
	commodity_alpha?: number;
	commodity_has_negative_price: boolean;
	strike_has_negative_price: boolean;
	has_base_options: boolean;
	leg_type?: string;
	cross_date?: string;

	negative_price_index?: {
		geco_id: number;
		name: string;
		orchestrade_id: number;
	};
	negative_price_threshold?: string;
	negative_price_operator?: string;
	negative_price_nb_consecutive_blocks?: string;

	curtailability_pricing_index?: {
		geco_id: number;
		name: string;
		orchestrade_id: number;
	};
	curtailability_forward_threshold?: string;
	curtailability_forward_operator?: string;
	curtailability_price_nb_consecutive_blocks?: string;
	is_nodal: boolean;
	payout_method?: PayoutMethod;
	cap_value?: number;
	floor_value?: number;
	payout_method_intraday?: PayoutMethod;
	cap_value_intraday?: number;
	floor_value_intraday?: number;
	apply_set_up_fees?: boolean;
	provision_type: string;
	pricing_add_ons: PricingAddOn[];
	spot_market: string;
	ppa_shape_types: PPAShapeTypes[];
}
